import { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GlobalAdminGuard } from "components/common/Guards/GlobalAdmin";
import { BottomNavigationBar } from "./BottomNavigationBar";
import { useAuth } from "../../../contexts/AuthContext";
import PanoramaViewer from "../../features/PanoramaViewer";
import { PlanAlignmentEditor } from "../PlanAlignmentEditor";
import { FrameStateEditor } from "../FrameStateEditor";
import ProgressReportModalViewer from "../../features/ProgressReportModalViewer";
import { MiniMap } from "../MiniMap";
import { useEditFrameState } from "../FrameStateEditor/hooks";
import { useEditPlanAlignmentState } from "../PlanAlignmentEditor/hooks";
import { useSmartImagePreloader } from "../../../hooks/useSmartImagePreloader";
import { useKeyAction } from "hooks/useKeyAction";
import { useKeyboardNavigation } from "./hooks";
import api from "../../../utils/api";
import { applyPlanAlignmentToPoint } from "utils/math/applyPlanAlignmentToPoint";
import { IMAGE_STATUS } from "../../../common/constants";
import { getDefaultCurrentFrameIndex } from "./utils";
import type { SetMinimapSize } from "../MiniMap/types";
import type { BBox2D } from "components/features/PanoramaViewer/types";
import { ChangeSelectedFrameIndex } from "../types";
import { DocumentationViewerProps, ProgressModalState } from "./types";

export const DocumentationViewer = ({
  targetFrame,
  targetObject,
  projectId,
  documentation,
  uploadPreviewImage,
  isUploadingPreviewImage,
  refetchDocumentation,
  isViewMode,
}: DocumentationViewerProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const filteredCameraFrames = useMemo(() => {
    return documentation?.frames.filter((frame) => {
      return !isViewMode || (isViewMode && frame.frameName === targetFrame);
    });
  }, [documentation?.frames, isViewMode, targetFrame]);

  const [currentFrameIndex, setCurrentFrameIndex] = useState(() =>
    getDefaultCurrentFrameIndex({
      cameraFrames: filteredCameraFrames,
      targetFrame,
    }),
  );

  const [minimapSize, setMinimapSize] = useState<"normal" | "large">(
    user?.isGlobalAdmin ? "large" : "normal",
  );

  const [debugCameraPositions, setDebugCameraPositions] = useState(false);

  const [currentImageStatus, setCurrentImageStatus] = useState(
    IMAGE_STATUS.LOADING,
  );

  const [comparedCurrentImageStatus, setComparedCurrentImageStatus] = useState(
    IMAGE_STATUS.LOADING,
  );

  const [progressReportModal, setProgressReportModal] =
    useState<ProgressModalState>({
      isOpen: false,
      data: null,
    });

  const sectionIdValue = useMemo(
    () => documentation?.sectionId?._id ?? documentation?.sectionId,
    [documentation?.sectionId],
  );

  const viewObjectInProgressReport = useCallback(
    (objectId: string) => {
      if (!projectId || !sectionIdValue) return;

      const url = `/project/${projectId}/sections/${sectionIdValue}/progress-report?object=${encodeURIComponent(
        objectId,
      )}`;

      window.open(url, "_blank", "noopener,noreferrer");
    },
    [projectId, sectionIdValue],
  );

  const handleCurrentImageError = useCallback(() => {
    setCurrentImageStatus(IMAGE_STATUS.ERROR);
  }, []);

  const handleComparedCurrentImageError = useCallback(() => {
    setComparedCurrentImageStatus(IMAGE_STATUS.ERROR);
  }, []);

  const cameraFrames = useMemo(
    () =>
      filteredCameraFrames.map((frame) => {
        const { opticalCenter, translation, ...currentFrame } = frame;
        return {
          ...currentFrame,
          processedPosition: opticalCenter,
          position: translation,
        };
      }) || [],
    [filteredCameraFrames],
  );

  const currentFrame = cameraFrames[currentFrameIndex];

  const imageUrls = useMemo(
    () =>
      cameraFrames.map((frame) =>
        api.getFrameImageUrl(projectId, documentation, frame.frameName),
      ),
    [cameraFrames, documentation, projectId],
  );

  // TODO: Legacy comment. Clarify what needs to be fixed.
  const comparedCameraPositions = cameraFrames;
  const comparedImageUrls = imageUrls;

  const showAdminTools = user?.isGlobalAdmin && !isViewMode;

  const {
    isEditingAlignment,
    isUpdatingAlignment,
    alignmentValues,
    setAlignmentValues,
    floorPlanAlignment,
    startEditingAlignment,
    cancelEditingAlignment,
    updatePlanAlignment,
  } = useEditPlanAlignmentState({
    documentationId: documentation._id,
    documentationPlanAlignment: documentation.planAlignment,
    refetchDocumentation,
  });

  const {
    isEditingFrameState,
    isUpdatingFrameState,
    editedFrameState,
    setEditedFrameFields,
    frameState,
    startEditingFrameState,
    cancelEditingFrameState,
    updateFrameState,
  } = useEditFrameState({
    currentFrame,
    fields: ["position", "processedPosition", "rotation"],
    refetchDocumentation,
  });

  const updatedCameraFrames = useMemo(() => {
    if (isEditingFrameState && editedFrameState) {
      return cameraFrames.map((frame, index) =>
        index === currentFrameIndex ? { ...frame, ...editedFrameState } : frame,
      );
    }
    return cameraFrames;
  }, [isEditingFrameState, editedFrameState, cameraFrames, currentFrameIndex]);

  const isAdminToolsActive = isEditingAlignment || isEditingFrameState;

  const { currentSrc, retryCurrentImage } = useSmartImagePreloader({
    images: imageUrls,
    currentIndex: currentFrameIndex,
    isCurrentImageLoading:
      currentImageStatus === IMAGE_STATUS.INITIAL ||
      currentImageStatus === IMAGE_STATUS.LOADING,
    onCurrentImageError: handleCurrentImageError,
  });

  const {
    currentSrc: comparedCurrentSrc,
    retryCurrentImage: comparedRetryCurrentImage,
  } = useSmartImagePreloader({
    images: comparedImageUrls,
    currentIndex: currentFrameIndex,
    isCurrentImageLoading:
      comparedCurrentImageStatus === IMAGE_STATUS.INITIAL ||
      comparedCurrentImageStatus === IMAGE_STATUS.LOADING,
    onCurrentImageError: handleComparedCurrentImageError,
  });

  const handleSetMinimapSize: SetMinimapSize = useCallback(
    (size) => {
      if (size === "normal") {
        if (isEditingAlignment) cancelEditingAlignment();
        if (isEditingFrameState) cancelEditingFrameState();
      }
      setMinimapSize(size);
    },
    [
      cancelEditingAlignment,
      cancelEditingFrameState,
      isEditingAlignment,
      isEditingFrameState,
    ],
  );

  const onEditAlignment = useCallback(() => {
    if (isEditingAlignment) {
      cancelEditingAlignment();
      return;
    }
    startEditingAlignment();
    cancelEditingFrameState();
  }, [
    isEditingAlignment,
    startEditingAlignment,
    cancelEditingAlignment,
    cancelEditingFrameState,
  ]);

  const onEditFrameState = useCallback(() => {
    if (isEditingFrameState) {
      cancelEditingFrameState();
      return;
    }
    startEditingFrameState();
    cancelEditingAlignment();
  }, [
    isEditingFrameState,
    startEditingFrameState,
    cancelEditingFrameState,
    cancelEditingAlignment,
  ]);

  const changeSelectedIndex: ChangeSelectedFrameIndex = useCallback(
    ({ newIndex, method }) => {
      console.log(
        `Changing selectedIndex from ${currentFrameIndex} to ${newIndex} (source: ${method})`,
      );

      if (newIndex >= 0 && newIndex < cameraFrames.length) {
        setCurrentFrameIndex(newIndex);
        return;
      }

      console.warn(
        `Invalid frame index ${newIndex}, valid range: 0-${
          cameraFrames.length - 1
        }`,
      );
    },
    [cameraFrames, currentFrameIndex],
  );

  const handleFrameChange = useCallback(
    (newIndex: number) => {
      changeSelectedIndex({
        newIndex,
        method: "panorama-click",
      });
    },
    [changeSelectedIndex],
  );

  const handleCurrentImageRetry = useCallback(() => {
    setCurrentImageStatus(IMAGE_STATUS.LOADING);
    retryCurrentImage();
  }, [retryCurrentImage]);

  const handleComparedCurrentImageRetry = useCallback(() => {
    setComparedCurrentImageStatus(IMAGE_STATUS.LOADING);
    comparedRetryCurrentImage();
  }, [comparedRetryCurrentImage]);

  const handleDrawDone = useCallback(
    (bbox: BBox2D) => {
      // TODO: understand when do we fire this callback
      console.log("handleDrawDone called with bbox:", bbox);
      if (isEditingFrameState) return;

      const currentFramePosition = {
        x: currentFrame.processedPosition
          ? currentFrame.processedPosition[0]
          : currentFrame.position[0],
        y: currentFrame.processedPosition
          ? currentFrame.processedPosition[1]
          : currentFrame.position[1],
        z: currentFrame.processedPosition
          ? currentFrame.processedPosition[2]
          : currentFrame.position[2],
      };

      const { x: svgX, y: svgY } = applyPlanAlignmentToPoint({
        point: { x: currentFramePosition.x, y: currentFramePosition.y },
        planAlignment: floorPlanAlignment,
      });

      setProgressReportModal({
        isOpen: true,
        data: {
          bbox,
          frameId: currentFrame._id,
          zoomX: svgX * 17,
          zoomY: svgY * 17,
          documentationId: documentation._id,
        },
      });
    },
    [isEditingFrameState, currentFrame, documentation, floorPlanAlignment],
  );

  const handleDoneObjectUpdate = useCallback(async () => {
    setProgressReportModal({ isOpen: false, data: null });
    refetchDocumentation();
  }, [refetchDocumentation]);

  useKeyboardNavigation({
    frameCount: cameraFrames.length,
    currentFrameIndex,
    changeSelectedFrameIndex: changeSelectedIndex,
    isDisabled: isAdminToolsActive,
  });

  useKeyAction({
    code: "KeyE",
    callback: onEditFrameState,
    options: {
      isDisabled: isAdminToolsActive || !showAdminTools,
    },
  });

  return (
    <>
      <div className="flex-grow flex flex-col overflow-hidden">
        <div className="flex-grow bg-gray-900 relative overflow-hidden">
          {currentFrame?.frameName && (
            <div className="relative w-full h-full">
              <PanoramaViewer
                navigationDisabled={true}
                cameraPositions={updatedCameraFrames}
                currentFrameIndex={currentFrameIndex}
                onFrameChange={handleFrameChange}
                debugCameraPositions={debugCameraPositions}
                currentSrc={currentSrc}
                currentImageStatus={currentImageStatus}
                setCurrentImageStatus={setCurrentImageStatus}
                retryCurrentImage={handleCurrentImageRetry}
                activeObjectId={targetObject}
                comparedCameraPositions={comparedCameraPositions}
                comparedCurrentSrc={comparedCurrentSrc}
                comparedCurrentImageStatus={comparedCurrentImageStatus}
                setComparedCurrentImageStatus={setComparedCurrentImageStatus}
                comparedRetryCurrentImage={handleComparedCurrentImageRetry}
                onDrawDone={handleDrawDone}
                isViewMode={isViewMode}
                onSelectObject={viewObjectInProgressReport}
              />
              {/* Debug Button Overlay - Top Center */}
              {!isViewMode && (
                <GlobalAdminGuard>
                  <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
                    <button
                      onClick={() =>
                        setDebugCameraPositions(!debugCameraPositions)
                      }
                      className={`px-3 py-1 rounded-lg text-sm shadow-lg ${
                        debugCameraPositions
                          ? "bg-orange-500 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {t("doc_viewer.debug_mode", {
                        status: debugCameraPositions
                          ? t("doc_viewer.debug_on")
                          : t("doc_viewer.debug_off"),
                      })}
                    </button>
                  </div>
                </GlobalAdminGuard>
              )}
              {!isViewMode && (
                <>
                  <MiniMap
                    showAdminTools={showAdminTools}
                    cameraFrames={updatedCameraFrames}
                    currentFrameIndex={currentFrameIndex}
                    planPreviewUrl={documentation.sectionId.plan.previewUrl}
                    debugCameraPositions={debugCameraPositions}
                    changeSelectedFrameIndex={changeSelectedIndex}
                    floorPlanAlignment={floorPlanAlignment}
                    onEditAlignment={onEditAlignment}
                    onEditFrameState={onEditFrameState}
                    uploadPreviewImage={uploadPreviewImage}
                    setEditedFrameFields={setEditedFrameFields}
                    isEditingAlignment={isEditingAlignment}
                    isEditingFrameState={isEditingFrameState}
                    isUploadingPreviewImage={isUploadingPreviewImage}
                    minimapSize={minimapSize}
                    setMinimapSize={handleSetMinimapSize}
                  />
                  {showAdminTools && (
                    <>
                      {isEditingAlignment && (
                        <PlanAlignmentEditor
                          alignmentValues={alignmentValues}
                          setAlignmentValues={setAlignmentValues}
                          handleUpdatePlanAlignment={updatePlanAlignment}
                          handleCancelEditingAlignment={cancelEditingAlignment}
                          isUpdatingAlignment={isUpdatingAlignment}
                        />
                      )}
                      {isEditingFrameState && (
                        <FrameStateEditor
                          frameName={currentFrame.frameName}
                          frame={frameState}
                          isUpdating={isUpdatingFrameState}
                          setEditedFrameFields={setEditedFrameFields}
                          handleCancelEditing={cancelEditingFrameState}
                          handleUpdate={updateFrameState}
                        />
                      )}
                    </>
                  )}
                  <BottomNavigationBar
                    frameCount={cameraFrames.length}
                    currentFrameIndex={currentFrameIndex}
                    changeSelectedFrameIndex={changeSelectedIndex}
                  />
                </>
              )}
            </div>
          )}
          {!currentFrame?.frameName && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400">
              <p>{t("doc_viewer.no_360_vis")}</p>
            </div>
          )}
        </div>
      </div>
      {progressReportModal.isOpen && (
        <ProgressReportModalViewer
          sectionData={documentation.sectionId}
          onClose={() => setProgressReportModal({ isOpen: false, data: null })}
          projectId={projectId}
          addedBboxInfo={progressReportModal.data}
          isObjectCreationMode={true}
          onDoneObjectUpdate={handleDoneObjectUpdate}
        />
      )}
    </>
  );
};
